<template>
  <div class="sub-user-container">
    <HomeHeader />
    <div class="w1200" id="main_content">
      <div class="sub-user-content">
        <!-- 工具栏 -->
        <div class="filter-bar">
          <span class="page-title">子用户管理</span>
          <div class="filter-spacer"></div>
          <el-button type="primary" @click="openCreate">+ 新增子用户</el-button>
        </div>

        <!-- 列表 -->
        <div class="table-wrapper">
          <el-table :data="subUsers" v-loading="listLoading" border stripe height="100%">
            <el-table-column label="序号" type="index" width="70" align="center" />
            <el-table-column prop="username" label="登录名" min-width="160" show-overflow-tooltip />
            <el-table-column prop="nickname" label="昵称" min-width="120" show-overflow-tooltip />
            <el-table-column label="加价策略" min-width="160">
              <template #default="{ row }">
                <el-tag v-if="row.cost_strategy_name" type="success">{{ row.cost_strategy_name }}</el-tag>
                <el-tag v-else type="danger">未绑定（登录会失败）</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="created_time" label="创建时间" width="180" align="center" />
            <el-table-column label="操作" width="140" align="center" fixed="right">
              <template #default="{ row }">
                <el-button type="primary" link @click="openReset(row)">
                  <el-icon><Refresh /></el-icon>重置密码
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <!-- 新增子用户弹窗 -->
    <el-dialog v-model="createVisible" title="新增子用户" width="520px" :close-on-click-modal="false">
      <el-form :model="createForm" label-width="90px">
        <el-form-item label="用户名" required>
          <el-input v-model="createForm.username_prefix" placeholder="自定义前缀" maxlength="30">
            <template #append>@{{ currentUserId }}</template>
          </el-input>
          <div class="form-tip">最终登录名为：前缀@{{ currentUserId }}</div>
        </el-form-item>
        <el-form-item label="昵称">
          <el-input v-model="createForm.nickname" placeholder="选填，默认取用户名前缀" maxlength="20" />
        </el-form-item>
        <el-form-item label="登录密码" required>
          <el-input v-model="createForm.password" type="password" placeholder="请输入登录密码" show-password />
        </el-form-item>
        <el-form-item label="加价策略" required>
          <el-select v-model="createForm.cost_strategy_id" placeholder="必须指定加价策略，否则无法登录"
                     style="width: 100%" :loading="strategyLoading">
            <el-option v-for="s in strategyOptions" :key="s.id"
                       :label="`${s.name}（${s.charge_type_display}）`" :value="s.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="handleCreate">确定</el-button>
      </template>
    </el-dialog>

    <!-- 重置密码弹窗 -->
    <el-dialog v-model="resetVisible" title="重置密码" width="420px" :close-on-click-modal="false">
      <el-form label-width="80px">
        <el-form-item label="子用户">
          <span>{{ resetTarget?.username }}</span>
        </el-form-item>
        <el-form-item label="新密码" required>
          <el-input v-model="newPassword" type="password" placeholder="请输入新密码" show-password />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="resetVisible = false">取消</el-button>
        <el-button type="primary" :loading="resetting" @click="handleReset">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { Refresh } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import HomeHeader from '@components/HomeHeader.vue'
import { postWlApi } from '@utils/api_v1.js'
import { useWuliu178AuthStore } from '@store/auth.js'

const wlAuth = useWuliu178AuthStore()
const currentUserId = computed(() => wlAuth.userProfile?.id ?? '')

// ---------- 列表 ----------
const subUsers = ref([])
const listLoading = ref(false)

const fetchList = async () => {
  listLoading.value = true
  try {
    const res = await postWlApi('/sub_user/list', {})
    if (res && res.code === 0) {
      subUsers.value = res.data.items || []
    } else {
      ElMessage.error(res?.message || '获取子用户列表失败')
    }
  } catch (e) {
    console.error(e)
    ElMessage.error('获取子用户列表失败')
  } finally {
    listLoading.value = false
  }
}

// ---------- 加价策略选项 ----------
const strategyOptions = ref([])
const strategyLoading = ref(false)

const fetchStrategies = async () => {
  strategyLoading.value = true
  try {
    const res = await postWlApi('/cost_strategy/list', { page: 1, page_size: 200 })
    if (res && res.code === 0) {
      strategyOptions.value = res.data.items || []
    }
  } catch (e) {
    console.error(e)
  } finally {
    strategyLoading.value = false
  }
}

// ---------- 新增 ----------
const createVisible = ref(false)
const creating = ref(false)
const createForm = reactive({
  username_prefix: '',
  nickname: '',
  password: '',
  cost_strategy_id: null
})

const openCreate = () => {
  createForm.username_prefix = ''
  createForm.nickname = ''
  createForm.password = ''
  createForm.cost_strategy_id = null
  if (strategyOptions.value.length === 0) fetchStrategies()
  createVisible.value = true
}

const handleCreate = async () => {
  if (!createForm.username_prefix.trim()) {
    ElMessage.warning('请输入用户名前缀')
    return
  }
  if (!createForm.password) {
    ElMessage.warning('请输入登录密码')
    return
  }
  if (!createForm.cost_strategy_id) {
    ElMessage.warning('必须为子用户指定加价策略')
    return
  }
  creating.value = true
  try {
    const res = await postWlApi('/sub_user/create', {
      username_prefix: createForm.username_prefix.trim(),
      nickname: createForm.nickname.trim(),
      password: createForm.password,
      cost_strategy_id: createForm.cost_strategy_id
    })
    if (res && res.code === 0) {
      ElMessage.success(`子用户创建成功，登录名：${res.data.sub_user.username}`)
      createVisible.value = false
      fetchList()
    } else {
      ElMessage.error(res?.message || '子用户创建失败')
    }
  } catch (e) {
    console.error(e)
    ElMessage.error('子用户创建失败')
  } finally {
    creating.value = false
  }
}

// ---------- 重置密码 ----------
const resetVisible = ref(false)
const resetting = ref(false)
const resetTarget = ref(null)
const newPassword = ref('')

const openReset = (row) => {
  resetTarget.value = row
  newPassword.value = ''
  resetVisible.value = true
}

const handleReset = async () => {
  if (!newPassword.value) {
    ElMessage.warning('请输入新密码')
    return
  }
  resetting.value = true
  try {
    const res = await postWlApi('/sub_user/reset_password', {
      sub_user_id: resetTarget.value.id,
      new_password: newPassword.value
    })
    if (res && res.code === 0) {
      ElMessage.success('密码重置成功')
      resetVisible.value = false
    } else {
      ElMessage.error(res?.message || '密码重置失败')
    }
  } catch (e) {
    console.error(e)
    ElMessage.error('密码重置失败')
  } finally {
    resetting.value = false
  }
}

onMounted(async () => {
  // 确保已加载用户信息（用于展示登录名后缀 @id）
  if (!wlAuth.userProfile) {
    try {
      const res = await postWlApi('/user/profile', {})
      if (res && res.data) wlAuth.setUserProfile(res.data)
    } catch (e) {
      console.error(e)
    }
  }
  fetchList()
})
</script>

<style scoped>
.sub-user-container {
  height: 100vh;
  overflow: hidden;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
}

#main_content.w1200 {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sub-user-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 20px;
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.filter-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.filter-spacer {
  flex: 1;
}

.table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 18px;
}
</style>
